import React, { useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './AddSignature.scss';
import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import { Box, Divider, Typography } from '@mui/material';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import DateUtils from '../../../utils/dateUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { TripStop } from '../../../redux/api/trips/trips.model';
import {
  useLazyGetPublicSignatureQuery,
  useSavePublicSignatureMutation,
  useLazyGetSignatureQuery,
  useSaveSignatureMutation,
  useGetStopSignatureSourceMutation,
  useGetStopSignatureSourceByTokenMutation
} from '../../../redux/api/orders/ordersApi';

interface AddSignatureProps {
  open: boolean;
  onClose: () => void;
  onSignatureSaved: () => void;
  stop: TripStop;
  loadId?: string;
  viewOnlyMode?: boolean;
  publicLoadToken?: string;
}

const AddSignature: React.FC<AddSignatureProps> = ({
  open,
  onClose,
  onSignatureSaved,
  stop,
  viewOnlyMode,
  publicLoadToken,
  loadId
}): JSX.Element => {
  const { addNotification } = useNotifications();

  const [signature, setSignature] = useState<any>(null);
  const [fullName, setFullName] = useState<string>('');

  const [showClearAction, setShowClearAction] = useState<boolean>(false);

  const [signatureId, setSignatureId] = useState<string | undefined>(undefined);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string>('');
  const [signatureDate, setSignatureDate] = useState<string>('');
  const [isSignatureLoading, setIsSignatureLoading] = useState<boolean>(true);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [getPublicSignature] = useLazyGetPublicSignatureQuery();
  const [savePublicSignature] = useSavePublicSignatureMutation();
  const [getSignatureForLoggedUser] = useLazyGetSignatureQuery();
  const [saveSignatureForLoggedUser] = useSaveSignatureMutation();
  const [getSignatureSourceForLoggedUser] = useGetStopSignatureSourceMutation();
  const [getSignatureSourceForPublicUser] = useGetStopSignatureSourceByTokenMutation();

  const {
    data: { userData }
  } = useAuth();

  /**
   * Gets the signature and full name with the stopId
   * */
  const getSignature = async (): Promise<void> => {
    const stopOrderId = loadId ? loadId : stop.order_id ? stop.order_id : stop.id;

    try {
      const signatureResponse =
        publicLoadToken && stop.id
          ? await getPublicSignature({ token: publicLoadToken, stopId: stop.id }).unwrap()
          : stopOrderId && stop.id
          ? await getSignatureForLoggedUser({ orderId: stopOrderId, stopId: stop.id }).unwrap()
          : null;

      if (!signatureResponse) return;

      const { id, full_name, created_at } = signatureResponse.data;

      setSignatureId(id);
      setFullName(full_name);
      setSignatureDate(
        DateUtils.splitDateAndTime(created_at, stop.location?.timezone || userData?.short_offset)
      );
    } catch (error) {
      addNotification({
        variant: 'error',
        message: 'Error getting signature'
      });
    }
  };

  /**
   * Gets the signature image for the signature key
   * @param key
   */
  const setSignatureSrc = async (signatureId: string): Promise<void> => {
    const stopOrderId = loadId ? loadId : stop.order_id ? stop.order_id : stop.id;
    try {
      setIsSignatureLoading(true);
      const imgBlob = publicLoadToken
        ? await getSignatureSourceForPublicUser({
            token: publicLoadToken,
            stopId: stop.id || '',
            signatureId
          }).unwrap()
        : stopOrderId && stop.id
        ? await getSignatureSourceForLoggedUser({
            orderId: stopOrderId || '',
            stopId: stop.id || '',
            signatureId
          }).unwrap()
        : null;

      if (!imgBlob) return;

      setSignaturePreviewUrl(URL.createObjectURL(imgBlob));
    } catch (error) {
      addNotification({
        variant: 'error',
        message: 'Error getting signature'
      });
    } finally {
      setIsSignatureLoading(false);
    }
  };

  useEffect(() => {
    if (signatureId) setSignatureSrc(signatureId);
  }, [signatureId]);

  // If viewOnlyMode is true, then we need to get the signature and full name with the stopId
  useEffect(() => {
    if (viewOnlyMode && open) {
      // Get the signature and full name with the stopId
      getSignature();
    }
  }, [viewOnlyMode, stop.id, publicLoadToken, loadId, open]);

  /**
   * Clears the signature drawn on the canvas
   */
  const clearSignature = (): void => {
    signature.clear();
    setShowClearAction(false);
  };

  /**
   * Saves signature
   */
  const saveSignature = async (): Promise<void> => {
    const signatureUrl = signature.getTrimmedCanvas().toDataURL('image/png');

    // Turn data URL to File object
    const signatureFile = await fetch(signatureUrl).then((res) => res.blob());
    const signatureData = {
      signatureFile,
      fullName
    };

    if (signature.isEmpty() || !fullName) {
      addNotification({
        variant: 'error',
        message:
          signature.isEmpty() && !fullName
            ? 'Please add your full name and signature to submit'
            : !fullName
            ? 'Please add your full name to submit'
            : 'Please add your signature to submit'
      });

      return;
    }

    try {
      setDisableSubmit(true);

      const stopOrderId = loadId ? loadId : stop.order_id ? stop.order_id : stop.id;

      const signatureResponse =
        publicLoadToken && stop.id
          ? await savePublicSignature({
              token: publicLoadToken,
              stopId: stop.id,
              signatureFile: signatureData.signatureFile,
              fullname: signatureData.fullName
            }).unwrap()
          : stopOrderId && stop.id && !publicLoadToken
          ? await saveSignatureForLoggedUser({
              orderId: stopOrderId,
              stopId: stop.id,
              signatureFile: signatureData.signatureFile,
              fullname: signatureData.fullName
            }).unwrap()
          : null;

      if (!signatureResponse) return;

      addNotification({
        variant: 'success',
        message: 'Signature saved successfully'
      });

      onSignatureSaved();
      handleClose();
    } catch (error: any) {
      let message = 'Error saving signature';
      if (error.data.message.includes('confirm/accept')) {
        message = 'Stop cannot be updated. Please confirm/accept the trip to update it.';
      }

      addNotification({
        message,
        variant: 'error'
      });
    } finally {
      setDisableSubmit(false);
    }
  };

  const handleClose = (): void => {
    setSignature(null);
    setFullName('');
    setShowClearAction(false);
    setDisableSubmit(false);
    onClose();
  };

  const handleSubmit = (): void => {
    saveSignature();
  };

  return (
    <TwDialog
      open={open}
      title={viewOnlyMode ? 'View Signature' : 'Add Signature'}
      onClickClose={handleClose}
      actions={
        !viewOnlyMode
          ? [
              <TwButton
                key="1"
                fullwidth={false}
                type={TwButtonType.secondary}
                label="Cancel"
                action={handleClose}
              />,
              <TwButton
                key="2"
                fullwidth={false}
                type={TwButtonType.primary}
                label="Submit"
                action={handleSubmit}
                disabled={disableSubmit}
              />
            ]
          : undefined
      }
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
          {viewOnlyMode
            ? `The authorized representative verified the accuracy and condition of the shipment at the time of loading/unloading as per the BOL.`
            : `By signing, the authorized representative verifies the accuracy and condition of the
          shipment at the time of loading/unloading as per the BOL.`}
        </Typography>
      </Box>
      <div className="add-signature__container">
        {!viewOnlyMode ? (
          <>
            <SignatureCanvas
              penColor="black"
              canvasProps={{
                className: 'add-signature__canvas'
              }}
              ref={(ref): void => setSignature(ref)}
              clearOnResize={false}
              onBegin={(): void => {
                setShowClearAction(true);
              }}
            />
            {!viewOnlyMode && showClearAction && (
              <TwButton
                type={TwButtonType.text}
                label="Clear"
                action={clearSignature}
                className="add-signature__clear-button"
              />
            )}
            <TwField
              name="fullName"
              value={fullName}
              inputType={TwInputTypes.text}
              placeholder="Full Name"
              onInputChange={(
                e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
              ): void => setFullName(e.target.value)}
            />
          </>
        ) : (
          <Box display="flex" flexDirection="column" className="add-signature__view-mode-container">
            <div className="add-signature__view-signature">
              {!isSignatureLoading && <img src={signaturePreviewUrl} />}
            </div>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                fontWeight="400"
                fontSize="14px"
                color="#6B7280"
                marginTop="10px"
              >
                {fullName}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="400"
                fontSize="14px"
                color="#6B7280"
                marginTop="10px"
              >
                {signatureDate}
              </Typography>
            </Box>
          </Box>
        )}
      </div>
    </TwDialog>
  );
};

export default AddSignature;
